import { useQuery } from "@tanstack/react-query";
import { Loader2, Clock } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";

interface AuditEntry {
  id: string;
  tableName: string;
  action: "INSERT" | "UPDATE" | "DELETE" | string;
  userEmail: string | null;
  createdAt: string;
}

interface AuditTrailResponse {
  data: AuditEntry[];
  total: number;
}

interface AccountActivityPanelProps {
  accountId: string;
  testId?: string;
}

function formatAction(action: string): string {
  if (action === "INSERT") return "Created";
  if (action === "UPDATE") return "Updated";
  if (action === "DELETE") return "Deleted";
  return action;
}

function formatTableName(tableName: string): string {
  return tableName
    .replace(/^crm_x_/, "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function AccountActivityPanel({ accountId, testId = "panel-account-activity" }: AccountActivityPanelProps) {
  const { data, isLoading } = useQuery<AuditTrailResponse>({
    queryKey: ["/api/crm/audit-trail", accountId],
    enabled: !!accountId,
  });

  if (isLoading) {
    return (
      <Card data-testid={testId}>
        <CardContent className="pt-4">
          <div className="flex items-center justify-center py-6 text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Loading activity...
          </div>
        </CardContent>
      </Card>
    );
  }

  const entries = data?.data ?? [];

  if (entries.length === 0) {
    return (
      <Card data-testid={testId}>
        <CardContent className="pt-4">
          <div className="text-center py-6">
            <p className="text-sm text-muted-foreground">No activity recorded yet</p>
            <p className="text-xs text-muted-foreground mt-1">
              Activity will appear here as changes are made to this account
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid={testId}>
      <CardContent className="pt-4">
        <div className="space-y-3">
          {entries.map((entry) => (
            <div key={entry.id} className="border-b pb-3 last:border-b-0 last:pb-0" data-testid="account-activity-entry">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-medium">
                  {formatAction(entry.action)} {formatTableName(entry.tableName)}
                </p>
                <p className="text-xs text-muted-foreground">
                  {new Date(entry.createdAt).toLocaleDateString("en-AU")}
                </p>
              </div>
              <div className="mt-1 flex items-center gap-3 text-xs text-muted-foreground">
                <span>{entry.userEmail || "System"}</span>
                <span className="inline-flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {new Date(entry.createdAt).toLocaleTimeString("en-AU", {
                    hour: "numeric",
                    minute: "2-digit",
                    hour12: true,
                  })}
                </span>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
